import { useState } from "react";
import { Building, Check, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { WorkspaceList } from "@/hooks/services/pps/handlers/workspace-api";

import { useGetData } from "@/hooks/services/use-query-hooks";

import { WorkspacesResponse } from "@/hooks/services/pps/types/workspace.type";

import { useWorkSpaceStore } from "@/stores/store";

export function WorkspaceToggle() {
  const { workspace, setWorkspace } = useWorkSpaceStore();

  const selectedWorkspaceId = workspace?.id;

  const [isActive, setIsActive] = useState(false);
  const { data: workspaceData } = useGetData<WorkspacesResponse>(
    WorkspaceList({
      page: 1,
      pageSize: 100,
    }),
  );

  return (
    <Popover open={isActive} onOpenChange={setIsActive}>
      <PopoverTrigger>
        <Button
          intent="custom"
          design="outline"
          className={cn(
            "aero:justify-between aero:gap-3 aero:h-11 aero:px-3 aero:min-w-[200px] aero:bg-background aero:hover:bg-background/50 aero:transition-colors aero:rounded-lg aero:border aero:shadow-sm",
            isActive && "aero:bg-accent aero:border-brand-primary/20",
          )}
        >
          <div className="aero:flex aero:items-center aero:gap-2">
            <div
              className={
                "aero:w-6 aero:h-6 aero:rounded-lg aero:bg-brand-primary-50 aero:flex aero:items-center aero:justify-center"
              }
            >
              <Building className="aero:text-brand-primary-500 aero:h-[27px] aero:w-[27px] aero:p-0.5" />
            </div>
            <div className="aero:flex aero:flex-col aero:items-start aero:gap-0.5 aero:min-w-0">
              <span className="aero:text-[10px] aero:uppercase aero:tracking-wider aero:text-foreground aero:font-medium aero:leading-none">
                Workspace
              </span>
              <span className="aero:text-sm aero:font-semibold aero:leading-none aero:text-foreground aero:truncate aero:max-w-[140px]">
                {workspace.workspaceName}
              </span>
            </div>
          </div>
          <ChevronDown
            className={cn(
              "aero:h-4 aero:w-4 aero:text-brand-primary aero:transition-transform aero:flex-shrink-0",
              isActive && "aero:rotate-180",
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="aero:w-72 aero:p-2 aero:rounded-xl aero:shadow-lg aero:border"
      >
        <div className="aero:space-y-1">
          <div className="aero:px-2 aero:py-1.5">
            <h4 className="aero:text-xs aero:uppercase aero:tracking-wider aero:font-semibold aero:text-neutral-500">
              Switch Workspace
            </h4>
          </div>
          <div className="aero:space-y-0.5">
            {workspaceData?.data?.map((workspace) => (
              <Button
                key={workspace.id}
                intent="custom"
                design="text"
                size="sm"
                className={cn(
                  "aero:w-full aero:justify-between aero:h-10 aero:px-3 aero:rounded-lg aero:hover:bg-accent/80 aero:transition-all",
                  selectedWorkspaceId === workspace.id && "aero:bg-accent",
                )}
                onClick={() => {
                  setWorkspace(workspace);
                  setIsActive(false);
                }}
              >
                <div className="aero:flex aero:items-center aero:gap-3 aero:min-w-0">
                  <div
                    className={cn(
                      "aero:flex-shrink-0 aero:w-7 aero:h-7 aero:rounded-md aero:flex aero:items-center aero:justify-center",
                      selectedWorkspaceId === workspace.id
                        ? "aero:bg-brand-primary-50"
                        : "aero:bg-muted",
                    )}
                  >
                    <Building
                      className={cn(
                        "aero:h-3.5 aero:w-3.5",
                        selectedWorkspaceId === workspace.id
                          ? "aero:text-brand-primary-500"
                          : "aero:text-neutral-500",
                      )}
                    />{" "}
                  </div>
                  <span className="aero:!text-sm">
                    {workspace.workspaceName}
                  </span>
                </div>
                {selectedWorkspaceId === workspace.id && (
                  <Check className="aero:h-4 aero:w-4 aero:!text-brand-primary" />
                )}
              </Button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
